/**
 * The site's content, hard-coded.
 *
 * This replaces the DatoCMS source plugin. Text lives in the sibling modules
 * (etusivu.js, talo.js, blogposts.js, …) and is plain JavaScript you can edit
 * directly. Images live in src/content/images/ and are processed at build time
 * by gatsby-plugin-sharp, so `fluid` objects still behave the way the
 * components expect.
 *
 *   const { etusivu, logot } = useSiteContent();
 *
 * Every page calls this instead of running its own GraphQL query.
 */
import { graphql, useStaticQuery } from 'gatsby';
import { resolveAssets } from './assets';

import { etusivu } from './etusivu';
import { talo } from './talo';
import { tilaratkaisu } from './tilaratkaisu';
import { sijainti } from './sijainti';
import { tyylit } from './tyylit';
import { logot, sivusto, contact, lataaEsite, highlight } from './site';
import { kerrospohjat } from './kerrospohjat';
import { tilat } from './tilat';
import { blogposts, blogiSivu } from './blogposts';
import { sisustustyylit } from './sisustustyylit';

const content = {
  etusivu,
  talo,
  tilaratkaisu,
  sijainti,
  tyylit,
  logot,
  sivusto,
  contact,
  lataaEsite,
  highlight,
  kerrospohjat,
  tilat,
  blogposts,
  blogiSivu,
  sisustustyylit,
};

// The unresolved tree, for build-time code (gatsby-node) that has no images.
export default content;
export {
  etusivu,
  talo,
  tilaratkaisu,
  sijainti,
  tyylit,
  logot,
  sivusto,
  contact,
  lataaEsite,
  highlight,
  kerrospohjat,
  tilat,
  blogposts,
  blogiSivu,
  sisustustyylit,
};

// Asking for childImageSharp on an SVG or a PDF makes gatsby-plugin-sharp warn
// once per file, so the two kinds are fetched separately: rasters with their
// fluid data, everything else with just the URL it is used by. The extension
// lists are inlined because a GraphQL literal cannot reference a constant.
const imagesQuery = graphql`
  {
    rasters: allFile(
      filter: {
        sourceInstanceName: { eq: "content-images" }
        extension: { in: ["jpg", "jpeg", "png", "webp", "tif", "tiff"] }
      }
    ) {
      nodes {
        base
        publicURL
        childImageSharp {
          fluid(maxWidth: 1600, quality: 80) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
    others: allFile(
      filter: {
        sourceInstanceName: { eq: "content-images" }
        extension: { nin: ["jpg", "jpeg", "png", "webp", "tif", "tiff"] }
      }
    ) {
      nodes {
        base
        publicURL
      }
    }
  }
`;

// Resolving walks the whole content tree, so do it once per set of files.
let cache = null;
let cacheKey = null;

/**
 * All site content, with image references resolved to usable objects.
 */
export const useSiteContent = () => {
  const data = useStaticQuery(imagesQuery);
  const nodes = [...data.rasters.nodes, ...data.others.nodes];

  if (cache && cacheKey === data) return cache;

  const byName = new Map(nodes.map(node => [node.base, node]));
  cache = resolveAssets(content, byName);
  cacheKey = data;

  if (process.env.NODE_ENV !== 'production') warnAboutMissingImages(cache);

  return cache;
};

// Some originals were lost with the DatoCMS account and are still being
// recovered; say so rather than quietly rendering grey boxes.
const warnAboutMissingImages = resolved => {
  const missing = new Set();
  const walk = value => {
    if (Array.isArray(value)) return value.forEach(walk);
    if (value && typeof value === 'object') {
      if (value.missing === true && value.file) missing.add(value.file);
      return Object.keys(value).forEach(k => walk(value[k]));
    }
  };
  walk(resolved);

  if (missing.size) {
    console.warn(
      `[content] ${missing.size} image(s) are referenced but not in ` +
        'src/content/images/ — showing placeholders. ' +
        'Run `node scripts/recover-assets.js` or add the files by hand:\n  ' +
        [...missing].sort().join('\n  ')
    );
  }
};

/** A single blog post plus the two thumbnails shown beneath it. */
export const useBlogPost = slug => {
  const { blogposts: posts } = useSiteContent();
  const post = posts.find(p => p.slug === slug);
  const thumbs = posts.filter(p => p.slug !== slug).slice(0, 2);
  return { post, thumbs };
};

/** A single interior style plus the other styles, for the "other styles" row. */
export const useSisustustyyli = slug => {
  const { sisustustyylit: styles } = useSiteContent();
  const style = styles.find(s => s.slug === slug);
  const others = styles.filter(s => s.slug !== slug);
  return { style, others };
};
